import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Edit, Plus, MapPin, Search, ArrowUpDown, X } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "./config";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';
import DeleteNameConfirm, { deleteNameMatches } from '@/components/DeleteNameConfirm';

const API = BACKEND_URL;

export default function RegionsTab({ userRole, personnelId, regions: initialRegions, personnel, parks, onDataChange }) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin, isInChain } = _flagsCtx;

  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Yetki kontrolü — her aksiyon kendi yetkisine bağlı (olustur/duzenle/sil ayrı).
  // Önceden hepsi tek `canEdit`'e bağlıydı; bu yüzden sadece "Bölge Düzenle"
  // yetkisi olan kullanıcıda "Yeni Bölge Ekle" ve "Sil" de görünüyordu.
  const canEdit = has("bolge.duzenle");
  const canCreate = has("bolge.olustur");
  const canDelete = has("bolge.sil");
  
  // Update regions when prop changes
  useEffect(() => {
    if (initialRegions) {
      setRegions(initialRegions);
    }
  }, [initialRegions]);
  
  // Arama, Sıralama ve Filtreleme
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("default"); // "default" or "alphabetical"
  const [roleFilter, setRoleFilter] = useState("all"); // "all", "bolge_muhendisi", "formen"
  
  // Region form state
  const [regionForm, setRegionForm] = useState({
    id: "",
    name: "",
    description: "",
    assigned_bolge_muhendisi_id: null,
    assigned_formen_ids: []
  });
  const [isRegionDialogOpen, setIsRegionDialogOpen] = useState(false);
  const [isEditingRegion, setIsEditingRegion] = useState(false);
  
  // Delete confirmation
  const [regionToDelete, setRegionToDelete] = useState(null);
  // Silme üçüncü onayı — diyalog her açılıp kapandığında sıfırlanır.
  const [regionDeleteConfirmText, setRegionDeleteConfirmText] = useState("");
  
  // Expanded parks state
  const [expandedRegionParks, setExpandedRegionParks] = useState({});

  // CRUD sonrası veriyi tazele.
  // ÖNEMLİ: Burada doğrudan GET /api/regions çağırıp setRegions YAPMIYORUZ.
  // O endpoint TÜM bölgeleri (filtrelenmemiş) döndürür; canEdit olan ama tüm
  // bölgeleri görmemesi gereken roller (örn. mudur_yardimcisi) için bu bir
  // görünürlük sızıntısıdır. Bunun yerine parent'ı yeniliyoruz; parent rol
  // bazlı kapsamı yeniden hesaplayıp scoped listeyi `initialRegions` prop'u
  // ile geri veriyor ve üstteki useEffect bunu state'e senkronluyor.
  const fetchRegions = async () => {
    try {
      await onDataChange();
    } catch (error) {
      console.error("Bölgeler yenilenirken hata:", error);
      toast.error("Bölge listesi yenilenemedi");
    }
  };

  // Reset form
  const resetRegionForm = () => {
    setRegionForm({
      id: "",
      name: "",
      description: "",
      assigned_bolge_muhendisi_id: null,
      assigned_formen_ids: []
    });
    setIsEditingRegion(false);
  };

  // Handle Formen toggle
  const handleFormenToggle = (formenId) => {
    setRegionForm(prev => {
      const isCurrentlyChecked = prev.assigned_formen_ids.includes(formenId);
      if (isCurrentlyChecked) {
        return {
          ...prev,
          assigned_formen_ids: prev.assigned_formen_ids.filter(id => id !== formenId)
        };
      } else {
        return {
          ...prev,
          assigned_formen_ids: [...prev.assigned_formen_ids, formenId]
        };
      }
    });
  };

  // Handle region submit
  const handleRegionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Scroll pozisyonunu kaydet - main content container için
    const mainContent = document.querySelector('.flex-1.overflow-auto');
    const savedScrollTop = mainContent ? mainContent.scrollTop : 0;
    
    try {
      if (isEditingRegion) {
        await axios.put(`${API}/api/regions/${regionForm.id}`, {
          name: regionForm.name,
          description: regionForm.description,
          assigned_bolge_muhendisi_id: regionForm.assigned_bolge_muhendisi_id,
          assigned_formen_ids: regionForm.assigned_formen_ids
        });
        toast.success("Bölge güncellendi");
      } else {
        await axios.post(`${API}/api/regions`, {
          name: regionForm.name,
          description: regionForm.description,
          assigned_bolge_muhendisi_id: regionForm.assigned_bolge_muhendisi_id,
          assigned_formen_ids: regionForm.assigned_formen_ids
        });
        toast.success("Bölge eklendi");
      }
      await fetchRegions(); // This will trigger onDataChange inside
      setIsRegionDialogOpen(false);
      resetRegionForm();
      
      // Scroll pozisyonunu restore et
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (mainContent) mainContent.scrollTop = savedScrollTop;
        }, 100);
      });
    } catch (error) {
      console.error("Bölge kaydedilemedi:", error);
      const errorDetail = error?.response?.data?.detail;
      toast.error(typeof errorDetail === "string" ? errorDetail : "Bölge kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  // Handle region edit
  const handleRegionEdit = (region) => {
    setRegionForm({
      id: region.id,
      name: region.name,
      description: region.description || "",
      assigned_bolge_muhendisi_id: region.assigned_bolge_muhendisi_id || null,
      assigned_formen_ids: region.assigned_formen_ids || []
    });
    setIsEditingRegion(true);
    setIsRegionDialogOpen(true);
  };

  useEffect(() => {
    setRegionDeleteConfirmText("");
  }, [regionToDelete]);

  const regionDeleteConfirmed =
    !!regionToDelete && deleteNameMatches(regionDeleteConfirmText, regionToDelete.name);

  // Handle region delete
  const handleRegionDelete = async () => {
    // Üçüncü onay: bölge adı birebir yazılmadan istek gitmez.
    if (!regionToDelete || !regionDeleteConfirmed) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/api/regions/${regionToDelete.id}`);
      toast.success("Bölge silindi");
      await fetchRegions(); // This will trigger onDataChange inside
      setRegionToDelete(null);
    } catch (error) {
      console.error("Bölge silinemedi:", error);
      // Backend'in anlamlı hata mesajını (örn. "Bu bölgede N aktif park var…")
      // kullanıcıya göster; yoksa genel mesaja düş.
      const errorDetail = error?.response?.data?.detail;
      toast.error(typeof errorDetail === "string" ? errorDetail : "Bölge silinemedi");
    } finally {
      setLoading(false);
    }
  };

  // Get Bölge Mühendisi personnel only
  const bolgeMuhendisiPersonnel = useMemo(() => 
    personnel.filter(p => p.role === 'bolge_muhendisi'),
    [personnel]
  );
  
  // Get Formen personnel only
  const formenPersonnel = useMemo(
    () =>
      personnel
        .filter(p => p.role === 'formen')
        .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr")),
    [personnel]
  );

  // Filtre dropdown'ı kapsamı: admin/müdür herkesi görür; diğer roller yalnızca
  // kendileri + kendi alt zincirlerindeki kişileri görür. Böylece formen/BM/MY
  // kendine bağlı olmayan rolleri/kişileri filtrede görmez.
  const scopedPersonnel = useMemo(() => {
    if (isAdmin || isMudur) return personnel;
    return personnel.filter(p => (isInChain ? isInChain(p.id) : false));
  }, [personnel, isAdmin, isMudur, isInChain]);
  
  // Get parks for a region
  const getRegionParks = (regionId) => {
    return parks.filter(p => p.region_id === regionId);
  };
  
  // Calculate total area for a region
  const calculateRegionStats = (regionId) => {
    const regionParks = getRegionParks(regionId);
    const totalArea = regionParks.reduce((sum, park) => sum + (park.area || 0), 0);
    const totalGrassArea = regionParks.reduce((sum, park) => sum + (park.grass_area || 0), 0);
    return { parkCount: regionParks.length, totalArea, totalGrassArea };
  };

  // Filtreleme ve sıralama
  const getFilteredAndSortedRegions = () => {
    let filtered = regions;
    
    // Arama filtresi
    if (searchTerm.trim()) {
      filtered = filtered.filter(region =>
        normalizeSearchText(region.name).includes(normalizeSearchText(searchTerm))
      );
    }
    
    // Rol filtresi
    if (roleFilter !== "all") {
      if (roleFilter === "unassigned") {
        // Atanmamış bölgeler (ne Bölge Mühendisi ne de Formen atanmamış)
        filtered = filtered.filter(region => 
          !region.assigned_bolge_muhendisi_id && 
          (!region.assigned_formen_ids || region.assigned_formen_ids.length === 0)
        );
      } else if (roleFilter.startsWith("mudur_yardimcisi:")) {
        // Belirli bir Müdür Yardımcısı'nın sorumlu olduğu bölgeler
        const personnelId = roleFilter.split(":")[1];
        // Müdür Yardımcısı tüm bölgelerden sorumlu olduğu için tüm bölgeleri göster
        // Veya Müdür Yardımcısı'na bağlı Bölge Mühendisi/Formen'lerin bölgelerini göster
        const relatedPersonnel = personnel.filter(p => p.mudur_yardimcisi_id === personnelId);
        const relatedBolgeMuhendisleri = relatedPersonnel.filter(p => p.role === 'bolge_muhendisi').map(p => p.id);
        const relatedFormenler = relatedPersonnel.filter(p => p.role === 'formen').map(p => p.id);
        
        filtered = filtered.filter(region => 
          relatedBolgeMuhendisleri.includes(region.assigned_bolge_muhendisi_id) ||
          (region.assigned_formen_ids && region.assigned_formen_ids.some(id => relatedFormenler.includes(id)))
        );
      } else if (roleFilter.startsWith("bolge_muhendisi:")) {
        // Belirli bir Bölge Mühendisi'nin bölgeleri
        const personnelId = roleFilter.split(":")[1];
        filtered = filtered.filter(region => region.assigned_bolge_muhendisi_id === personnelId);
      } else if (roleFilter.startsWith("formen:")) {
        // Belirli bir Formen'in bölgeleri
        const personnelId = roleFilter.split(":")[1];
        filtered = filtered.filter(region => 
          region.assigned_formen_ids && region.assigned_formen_ids.includes(personnelId)
        );
      }
    }
    
    // Sıralama
    if (sortOrder === "alphabetical") {
      // Gerçek alfabetik sıralama: Bölge adına göre A-Z
      filtered = [...filtered].sort((a, b) => {
        return String(a?.name || "").localeCompare(String(b?.name || ""), "tr");
      });
    }
    
    return filtered;
  };
  
  // Bölgedeki Formenleri getir
  const getRegionForemen = (regionId) => {
    // Check both new field (formen_region_id) and old field (region_ids) for backward compatibility
    return personnel.filter(p => 
      p.role === 'formen' && 
      (p.formen_region_id === regionId || 
       (p.region_ids && p.region_ids.includes(regionId)))
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Bölge Yönetimi
                {regions.length > 0 && (
                  <Badge variant="default" className="text-sm px-3 py-1 ml-1">
                    <span className="font-bold">{getFilteredAndSortedRegions().length}</span>
                    <span className="mx-1">/</span>
                    <span className="opacity-75">{regions.length}</span>
                    <span className="ml-1">Bölge</span>
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Bölgeleri yönetin, Bölge Mühendisi ve parkları atayın
              </CardDescription>
            </div>
            {canCreate && (
              <Button
                onClick={() => {
                  resetRegionForm();
                  setIsRegionDialogOpen(true);
                }}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Yeni Bölge Ekle
              </Button>
            )}
          </div>
          
          {/* Arama, Rol Filtresi ve Sıralama */}
          {regions.length > 0 && (
            <div className="flex gap-2 mt-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Bölge ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <SearchableSelect
                value={roleFilter}
                onChange={setRoleFilter}
                allLabel="Tüm Roller"
                searchPlaceholder="Rol / kişi ara..."
                className="w-[250px]"
                options={[
                  { id: "unassigned", name: "Atama Yapılmamış" },
                  ...scopedPersonnel
                    .filter(p => p.role === 'mudur_yardimcisi')
                    .map(p => ({ id: `mudur_yardimcisi:${p.id}`, name: `👔 ${p.name}`, group: "Müdür Yardımcıları" })),
                  ...scopedPersonnel
                    .filter(p => p.role === 'bolge_muhendisi')
                    .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), 'tr'))
                    .map(p => ({ id: `bolge_muhendisi:${p.id}`, name: `🎓 ${p.name}`, group: "Bölge Mühendisleri" })),
                  ...scopedPersonnel
                    .filter(p => p.role === 'formen')
                    .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), 'tr'))
                    .map(p => ({ id: `formen:${p.id}`, name: `👷 ${p.name}`, group: "Formenler" })),
                ]}
              />
              
              <Button
                variant={sortOrder === "alphabetical" ? "default" : "outline"}
                onClick={() => setSortOrder(sortOrder === "default" ? "alphabetical" : "default")}
                className="flex items-center gap-2"
              >
                <ArrowUpDown className="h-4 w-4" />
                {sortOrder === "alphabetical" ? "A-Z" : "Sırala"}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {regions.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Henüz bölge eklenmemiş</p>
              <Button 
                onClick={() => {
                  resetRegionForm();
                  setIsRegionDialogOpen(true);
                }}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                İlk Bölgeyi Ekle
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFilteredAndSortedRegions().length === 0 ? (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-500">
                    "{searchTerm}" araması için sonuç bulunamadı
                  </p>
                </div>
              ) : (
                getFilteredAndSortedRegions().map(region => {
                  const stats = calculateRegionStats(region.id);
                  const regionEngineer = personnel.find(p => p.id === region.assigned_bolge_muhendisi_id);
                  // Formen/park görevlisi gibi dar kapsamlı rollerde ÜST AMİR
                  // `personnel` listesinde bulunmaz (kapsam kendi astlarıyla
                  // sınırlı). Sadece id→isim eşlemesine bakılınca atanmış BM
                  // "Atanmamış" görünüyordu. Backend zaten ismi denormalize
                  // ediyor (regions.denorm.assigned_bolge_muhendisi_name) —
                  // eşleme tutmazsa ona düşülüyor.
                  const regionEngineerName =
                    regionEngineer?.name || region.assigned_bolge_muhendisi_name || "";
                  // Get Formenler from assigned_formen_ids
                  const regionForemen = (region.assigned_formen_ids || [])
                    .map(formenId => personnel.find(p => p.id === formenId))
                    .filter(Boolean);
                  
                  return (
                    <Card key={region.id} className="border-rose-200 hover:shadow-lg transition-shadow">
                    <CardHeader className="bg-rose-50 pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-rose-700 text-lg">{region.name}</CardTitle>
                          {region.description && (
                            <CardDescription className="text-xs mt-1">{region.description}</CardDescription>
                          )}
                        </div>
                        {(canEdit || canDelete) && (
                          <div className="flex gap-1">
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRegionEdit(region)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setRegionToDelete(region)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-3">
                      {/* Bölge Mühendisi ve Formenler - HİYERARŞİK GÖRÜNÜM */}
                      <div className="space-y-2">
                        {/* Bölge Mühendisi */}
                        <div className="flex items-center gap-2 text-sm">
                          <Badge className="text-xs bg-blue-500 text-white border-blue-600">
                            Bölge Mühendisi
                          </Badge>
                          {regionEngineerName ? (
                            <span className="font-medium text-gray-800">{regionEngineerName}</span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">Atanmamış</span>
                          )}
                        </div>
                        
                        {/* Bölge Formenleri - Hiyerarşik */}
                        {regionForemen.length > 0 && (
                          <div className="border-t pt-2 space-y-2">
                            {regionForemen.map(formen => (
                              <div key={formen.id} className="flex items-center gap-2 text-sm">
                                <span className="text-xs text-gray-600">Bağlı:</span>
                                <Badge className="text-xs bg-yellow-500 text-white border-yellow-600">
                                  Formen
                                </Badge>
                                <span className="font-medium text-gray-800">{formen.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* İstatistikler */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-gray-50 p-2 rounded text-center">
                          <p className="text-xs text-gray-600">Park</p>
                          <p className="text-lg font-bold text-gray-900">{stats.parkCount}</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded text-center">
                          <p className="text-xs text-green-600">Alan</p>
                          <p className="text-sm font-bold text-green-900">{stats.totalArea.toLocaleString('tr-TR')} m²</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded text-center">
                          <p className="text-xs text-green-600">Çim</p>
                          <p className="text-sm font-bold text-green-900">{stats.totalGrassArea.toLocaleString('tr-TR')} m²</p>
                        </div>
                      </div>
                      
                      {/* Parklar */}
                      {stats.parkCount > 0 && (
                        <div className="border-t pt-2">
                          <p className="text-xs text-gray-600 mb-1">Parklar:</p>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              const regionParks = getRegionParks(region.id);
                              // Alfabetik sıralama (Türkçe karakter desteği ile)
                              const sortedParks = [...regionParks].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), 'tr'));
                              const isExpanded = expandedRegionParks[region.id] || false;
                              const displayParks = isExpanded ? sortedParks : sortedParks.slice(0, 3);
                              
                              return (
                                <>
                                  {displayParks.map(park => (
                                    <Badge key={park.id} variant="secondary" className="text-xs">
                                      {park.name}
                                    </Badge>
                                  ))}
                                  {stats.parkCount > 3 && (
                                    <Badge 
                                      variant="outline" 
                                      className="text-xs cursor-pointer hover:bg-gray-100"
                                      onClick={() => setExpandedRegionParks(prev => ({ ...prev, [region.id]: !isExpanded }))}
                                    >
                                      {isExpanded ? 'Daha az göster' : `+${stats.parkCount - 3} daha`}
                                    </Badge>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Region Form Dialog */}
      <DraggableModal
        isOpen={isRegionDialogOpen}
        onClose={() => {
          setIsRegionDialogOpen(false);
          resetRegionForm();
        }}
        title={isEditingRegion ? "Bölge Düzenle" : "Yeni Bölge Ekle"}
        subtitle="Bölge bilgilerini girin ve Bölge Mühendisi atayın"
        maxWidth="max-w-2xl"
      >
          <form onSubmit={handleRegionSubmit}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="region-name">Bölge Adı *</Label>
                <Input
                  id="region-name"
                  value={regionForm.name}
                  onChange={(e) => setRegionForm({ ...regionForm, name: e.target.value })}
                  placeholder="Örn: Kuzey Bölgesi"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="region-description">Açıklama</Label>
                <Input
                  id="region-description"
                  value={regionForm.description}
                  onChange={(e) => setRegionForm({ ...regionForm, description: e.target.value })}
                  placeholder="Bölge hakkında kısa açıklama"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="region-engineer">Bölge Mühendisi</Label>
                <SearchableSelect
                  value={regionForm.assigned_bolge_muhendisi_id || "none"}
                  onChange={(value) => setRegionForm({ ...regionForm, assigned_bolge_muhendisi_id: value === "none" ? null : value })}
                  allLabel="Atama yapma"
                  allValue="none"
                  searchPlaceholder="Bölge Mühendisi ara..."
                  options={bolgeMuhendisiPersonnel.map(person => ({ id: person.id, name: person.name }))}
                />
                <p className="text-xs text-gray-500">
                  Sadece Bölge Mühendisi rolündeki personel görüntülenir
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Bölge Formenleri</Label>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 bg-white">
                  {formenPersonnel.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Henüz formen bulunmuyor</p>
                  ) : (
                    formenPersonnel.map(formen => {
                      const isChecked = regionForm.assigned_formen_ids.includes(formen.id);
                      return (
                        <div 
                          key={formen.id} 
                          className="flex items-center space-x-3 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => handleFormenToggle(formen.id)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border-2 border-gray-400 rounded">
                            {isChecked && (
                              <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm font-medium flex-1 select-none">
                            {formen.name}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Birden fazla formen seçebilirsiniz
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsRegionDialogOpen(false);
                  resetRegionForm();
                }}
              >
                İptal
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Kaydediliyor..." : (isEditingRegion ? "Güncelle" : "Ekle")}
              </Button>
            </div>
          </form>
      </DraggableModal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!regionToDelete} onOpenChange={(open) => !open && setRegionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bölgeyi silmek istediğinizden emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{regionToDelete?.name}</strong> bölgesi silinecek ve bu bölgeye atanmış Bölge Mühendisi / Formen atamaları temizlenecek.
              Bölgede aktif park varsa silme engellenir; önce parkları başka bir bölgeye taşımanız gerekir. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <DeleteNameConfirm
            name={regionToDelete?.name}
            value={regionDeleteConfirmText}
            onChange={setRegionDeleteConfirmText}
            label="bölgenin adını"
          />

          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRegionDelete}
              disabled={!regionDeleteConfirmed}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
